/**
 * World-layer sprite sync: structures (registry art + placeholders),
 * painter-ordered, interpolated, cached per state (doc 12: render a state once,
 * blit forever).
 *
 * The fauna half lives in `faunaview.ts` — since stage 7a a creature carries
 * five states, a hit flash and a body that outlives its sim entity, which is a
 * system of its own rather than a second loop in here.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import { buildEntity } from '../art/build.ts';
import { applyFlash, applyRise, BUILD_TICKS, FLASH_TICKS } from '../art/construction.ts';
import type { EntityState, Tiers } from '../art/entity.ts';
import {
  drawCoreDome,
  drawEconomyBuilding,
  drawWall,
  WALL_NE,
  WALL_NW,
  WALL_SE,
  WALL_SW,
  type PlaceholderRender,
} from '../art/placeholder.ts';
import { REGISTRY_DEFS, specById } from '../art/registry.ts';
import { FaunaView } from './faunaview.ts';
import { blockCentre, TH, TW } from '../render/iso.ts';
import type { DepthPalette } from '../render/palette.ts';
import { TextureCache } from '../render/pipeline.ts';
import { PixelSurface } from '../render/surface.ts';
import { towerStats } from '../sim/defs.ts';
import type { Sim } from '../sim/sim.ts';
import { SPECIES } from '../sim/species.ts';
import type { Structure } from '../sim/types.ts';

interface StructView {
  ground: Sprite;
  body: Sprite;
  key: string;
}

export class WorldView {
  private readonly structViews = new Map<number, StructView>();
  private readonly renderCache = new Map<string, PlaceholderRender>();
  /** Everything that swims. Kept as a field so the two halves share one layer. */
  readonly fauna: FaunaView;
  /** Structures still rising out of the seabed: id → tick placement finished. */
  private readonly rising = new Map<number, number>();
  /** Structures flashing an upgrade: id → tick the flash ends. */
  private readonly flashing = new Map<number, number>();

  private readonly sim: () => Sim;
  private readonly padLayer: Container;
  private readonly bodyLayer: Container;
  private readonly cache: TextureCache;
  private readonly depth: DepthPalette;

  constructor(
    sim: () => Sim,
    padLayer: Container,
    bodyLayer: Container,
    cache: TextureCache,
    depth: DepthPalette,
    flash: () => number,
  ) {
    this.sim = sim;
    this.padLayer = padLayer;
    this.bodyLayer = bodyLayer;
    this.cache = cache;
    this.depth = depth;
    this.fauna = new FaunaView(sim, bodyLayer, cache, depth, flash);
  }

  markRiled(waveId: number): void {
    this.fauna.markRiled(waveId);
  }

  /** From the `enemyHit` event: the creature that took it flashes (doc 10). */
  noteHit(enemyId: number): void {
    this.fauna.noteHit(enemyId);
  }

  /** Doc 17 §7: a new building rises out of the seabed instead of appearing. */
  markPlaced(structureId: number): void {
    this.rising.set(structureId, this.sim().state.tick + BUILD_TICKS);
  }

  /** Doc 17 §7: a bought tier is acknowledged on the model, not just the panel. */
  markUpgraded(structureId: number): void {
    this.flashing.set(structureId, this.sim().state.tick + FLASH_TICKS);
  }

  /** Look up (and cache) a structure's current render — picking needs it too. */
  render(st: Structure, hovered: boolean, selected: boolean): { r: PlaceholderRender; key: string } {
    return this.animated(st, this.renderBase(st, hovered, selected));
  }

  /**
   * The rise and the upgrade flash, layered over whatever the entity normally
   * looks like. Both are cached like any other frame, and both expire on their
   * own tick — a resumed save shows no animation, which is correct: nothing was
   * just built.
   */
  private animated(st: Structure, base: { r: PlaceholderRender; key: string }): { r: PlaceholderRender; key: string } {
    const tick = this.sim().state.tick;
    const riseEnd = this.rising.get(st.id);
    if (riseEnd !== undefined) {
      if (tick >= riseEnd) this.rising.delete(st.id);
      else {
        const step = Math.max(1, Math.round(((BUILD_TICKS - (riseEnd - tick)) / BUILD_TICKS) * 5));
        return this.cachedRender(`${base.key}#rise${step}`, () => risen(base.r, step / 5));
      }
    }
    const flashEnd = this.flashing.get(st.id);
    if (flashEnd !== undefined) {
      if (tick >= flashEnd) this.flashing.delete(st.id);
      else {
        const step = Math.max(1, Math.round(((flashEnd - tick) / FLASH_TICKS) * 3));
        return this.cachedRender(`${base.key}#flash${step}`, () => flashed(base.r, step / 3));
      }
    }
    return base;
  }

  private cachedRender(key: string, make: () => PlaceholderRender): { r: PlaceholderRender; key: string } {
    return this.cached(key, make);
  }

  private renderBase(st: Structure, hovered: boolean, selected: boolean): { r: PlaceholderRender; key: string } {
    const sim = this.sim();
    if (st.def === 'core_dome') {
      const damaged = st.hp < st.maxHp * 0.5;
      return this.cached(`core:${damaged}`, () => drawCoreDome(this.depth, damaged));
    }
    if (st.def === 'wall') {
      const mask = st.ruin ? 0 : this.wallMask(st.i, st.j);
      return this.cached(`wall:${st.ruin}:${mask}`, () => drawWall(this.depth, st.ruin, mask));
    }
    if (st.kind === 'tower') {
      const state = this.towerState(st, hovered, selected);
      let phase = 0.3;
      if (state === 'firing') phase = Math.min(1, (sim.state.tick - st.firedAt) / 9);
      const bucket = Math.round(phase * 3) / 3;
      // Zappy is omnidirectional: one facing serves all, which keeps its cache tiny
      const facing = st.def === 'zappy_boi' ? 1 : st.facing;
      const up: Tiers = { ...st.upgrades };
      const tiers = `${up.a}${up.b}${up.c}`;
      return this.cached(`${st.def}:${state}:${facing}:${bucket}:${tiers}`, () =>
        buildEntity(specById(st.def), { depth: this.depth, state, phase: bucket, facing, upgrades: up }),
      );
    }
    // economy status → visible state; brownout adds the doc 10/11 flicker
    const status = st.ruin ? 'ruin' : (sim.status.get(st.id) ?? 'stalled');
    const flicker =
      status === 'working' && sim.power.efficiency < 1 && ((sim.state.tick >> 3) & 1) === 1;
    const shown = flicker ? 'stalled' : status;
    if (st.def === 'ore_rig') {
      const state: EntityState =
        st.ruin ? 'destroyed'
        : selected ? 'selected'
        : hovered ? 'hover'
        : shown === 'offgrid' ? 'starved'
        : shown === 'working' ? 'working'
        : 'idle';
      const bucket = state === 'working' ? (Math.floor(sim.state.tick / 6) % 5) / 5 : 0.3;
      return this.cached(`ore_rig:${state}:${bucket}`, () =>
        buildEntity(specById('ore_rig'), { depth: this.depth, state, phase: bucket, facing: 1 }),
      );
    }
    return this.cached(`${st.def}:${shown}`, () => drawEconomyBuilding(st.def, this.depth, shown));
  }

  ghostRender(def: string, wallMaskNow: number): { r: PlaceholderRender; key: string } {
    const key = def === 'wall' ? `ghost:wall:${wallMaskNow}` : `ghost:${def}`;
    return this.cached(key, () => {
      if (def === 'wall') return drawWall(this.depth, false, wallMaskNow);
      if (REGISTRY_DEFS.has(def)) {
        return buildEntity(specById(def), { depth: this.depth, state: 'ghost', phase: 0, facing: 1 });
      }
      return drawEconomyBuilding(def, this.depth, 'working');
    });
  }

  private cached(key: string, make: () => PlaceholderRender): { r: PlaceholderRender; key: string } {
    let r = this.renderCache.get(key);
    if (!r) {
      r = make();
      this.renderCache.set(key, r);
    }
    return { r, key };
  }

  wallMask(i: number, j: number): number {
    const sim = this.sim();
    const isWall = (ni: number, nj: number): boolean => {
      if (!sim.grid.inBounds(ni, nj)) return false;
      const id = sim.grid.occ[sim.grid.idx(ni, nj)]!;
      if (id < 0) return false;
      const st = sim.byId(id);
      return st !== undefined && st.def === 'wall' && !st.ruin;
    };
    return (
      (isWall(i + 1, j) ? WALL_SE : 0) |
      (isWall(i, j + 1) ? WALL_SW : 0) |
      (isWall(i - 1, j) ? WALL_NW : 0) |
      (isWall(i, j - 1) ? WALL_NE : 0)
    );
  }

  private towerState(st: Structure, hovered: boolean, selected: boolean): EntityState {
    const sim = this.sim();
    if (st.ruin) return 'destroyed';
    if (selected) return 'selected';
    if (hovered) return 'hover';
    if (st.disabledUntil > sim.state.tick) return 'dry'; // hugged: dark and silent
    if (st.firedAt >= 0 && sim.state.tick - st.firedAt < 9) return 'firing';
    const stats = towerStats(st.def, st.upgrades);
    if (stats.ammoPerShot > 0 && sim.state.resources.ammo < stats.ammoPerShot) return 'dry';
    if (stats.powerPerShot > 0 && sim.state.capacitor < stats.powerPerShot) return 'dry';
    if (st.targetId >= 0) return 'tracking';
    return 'idle';
  }

  syncStructures(hoverId: number, selectedId: number): void {
    const sim = this.sim();
    const seen = new Set<number>();
    for (const st of sim.state.structures) {
      seen.add(st.id);
      let v = this.structViews.get(st.id);
      if (!v) {
        v = { ground: new Sprite(), body: new Sprite(), key: '' };
        this.padLayer.addChild(v.ground);
        this.bodyLayer.addChild(v.body);
        this.structViews.set(st.id, v);
      }
      const { r, key } = this.render(st, st.id === hoverId, st.id === selectedId);
      if (key !== v.key) {
        v.key = key;
        v.ground.texture = this.cache.get(`${key}#g`, () => r.ground.surface);
        v.body.texture = this.cache.get(`${key}#b`, () => r.body.surface);
      }
      const c = blockCentre(st.i, st.j, st.size, st.size);
      v.ground.x = c.x - r.ground.ax;
      v.ground.y = c.y - r.ground.ay;
      v.body.x = c.x - r.body.ax;
      v.body.y = c.y - r.body.ay;
      v.body.zIndex = st.i + st.j + (st.size - 1) * 2;
    }
    for (const [id, v] of this.structViews) {
      if (!seen.has(id)) {
        v.ground.destroy();
        v.body.destroy();
        this.structViews.delete(id);
      }
    }
  }

  syncEnemies(alpha: number): void {
    this.fauna.sync(alpha);
  }

  capturePrev(): void {
    this.fauna.capturePrev();
  }

  clear(): void {
    for (const v of this.structViews.values()) {
      v.ground.destroy();
      v.body.destroy();
    }
    this.structViews.clear();
    this.fauna.clear();
  }
}

/** A copy of `r` revealed to `p` of its height — the construction rise. */
function risen(r: PlaceholderRender, p: number): PlaceholderRender {
  const body = r.body.surface.clone();
  applyRise(body, r.body.ay, p);
  return { ground: r.ground, body: { ...r.body, surface: body.resetOps() } };
}

/** A copy of `r` with its silhouette flashed — the upgrade acknowledgement. */
function flashed(r: PlaceholderRender, k: number): PlaceholderRender {
  const body = r.body.surface.clone();
  applyFlash(body, k * 0.75);
  return { ground: r.ground, body: { ...r.body, surface: body.resetOps() } };
}
